import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Artikel, ArtikelKomponente } from '../types';
import CustomFieldsForm from './CustomFieldsForm';
import AktivitaetenBlock from './AktivitaetenBlock';
import './ArtikelForm.css';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

interface ArtikelFormProps {
  editingArtikel?: Artikel | null;
  onSuccess: () => void;
  onCancel: () => void;
}

interface SteuersatzOption {
  steuersatz_id: number;
  bezeichnung: string;
  prozent: number;
  ist_standard: boolean;
  aktiv: boolean;
}

const ArtikelForm: React.FC<ArtikelFormProps> = ({ editingArtikel, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    bezeichnung: '',
    variante: '',
    kategorie: 'ROHMATERIAL',
    einheit: 'm\u00B3',
    mindestbestand: '0',
    preis_vk: '',
    preis_ek: '',
    qualitaetsstufe: '',
    ist_verkaufsartikel: false,
    steuersatz_id: '',
    hinweistext: '',
    standard_aufschlag_prozent: '',
    kupfer_gewicht_kg_100m: '',
    kupfer_basis: '',
  });
  const [customAttributes, setCustomAttributes] = useState<Record<string, any>>({});
  const [steuersaetze, setSteuersaetze] = useState<SteuersatzOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const authToken = localStorage.getItem('auth_token') || '';

  // Stückliste
  const [komponenten, setKomponenten] = useState<ArtikelKomponente[]>([]);
  const [alleArtikel, setAlleArtikel] = useState<Artikel[]>([]);
  const [neueKomponente, setNeueKomponente] = useState({ komponente_id: '', menge: '1' });
  const [kompLoading, setKompLoading] = useState(false);

  useEffect(() => {
    if (editingArtikel) {
      setFormData({
        bezeichnung: editingArtikel.bezeichnung,
        variante: editingArtikel.variante || '',
        kategorie: editingArtikel.kategorie || 'ROHMATERIAL',
        einheit: editingArtikel.einheit,
        mindestbestand: String(editingArtikel.mindestbestand || 0),
        preis_vk: editingArtikel.preis_vk ? String(editingArtikel.preis_vk) : '',
        preis_ek: editingArtikel.preis_ek ? String(editingArtikel.preis_ek) : '',
        qualitaetsstufe: editingArtikel.qualitaetsstufe || '',
        ist_verkaufsartikel: editingArtikel.ist_verkaufsartikel || false,
        steuersatz_id: editingArtikel.steuersatz_id != null ? String(editingArtikel.steuersatz_id) : '',
        hinweistext: editingArtikel.hinweistext || '',
        standard_aufschlag_prozent: editingArtikel.standard_aufschlag_prozent != null
          ? String(editingArtikel.standard_aufschlag_prozent) : '',
        // 0 ist ein gueltiger Wert (Hohlpreis) — deshalb != null statt Truthiness
        kupfer_gewicht_kg_100m: editingArtikel.kupfer_gewicht_kg_100m != null
          ? String(editingArtikel.kupfer_gewicht_kg_100m) : '',
        kupfer_basis: editingArtikel.kupfer_basis != null
          ? String(editingArtikel.kupfer_basis) : '',
      });
      setKomponenten(editingArtikel.komponenten || []);
      setCustomAttributes((editingArtikel as any).custom_attributes || {});
    }
  }, [editingArtikel]);

  // Alle Artikel laden (für Komponenten-Auswahl)
  useEffect(() => {
    axios.get(`${API_URL}/api/artikel`).then(res => setAlleArtikel(res.data)).catch(() => {});
  }, []);

  // Steuersatz-Stammdaten laden; Neuanlage bekommt den Standardsatz vorbelegt
  useEffect(() => {
    axios.get<SteuersatzOption[]>(`${API_URL}/api/steuersaetze`)
      .then(res => {
        setSteuersaetze(res.data);
        if (!editingArtikel) {
          const standard = res.data.find(s => s.ist_standard && s.aktiv);
          if (standard) {
            setFormData(f => (f.steuersatz_id === '' ? { ...f, steuersatz_id: String(standard.steuersatz_id) } : f));
          }
        }
      })
      .catch(() => {});
  }, [editingArtikel]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.bezeichnung.trim()) {
      setError('Bezeichnung ist erforderlich');
      return;
    }

    setLoading(true);

    const payload: any = {
      bezeichnung: formData.bezeichnung,
      kategorie: formData.kategorie,
      einheit: formData.einheit,
      mindestbestand: parseFloat(formData.mindestbestand) || 0,
      ist_verkaufsartikel: formData.ist_verkaufsartikel,
    };
    if (formData.variante) payload.variante = formData.variante;
    if (formData.preis_vk) payload.preis_vk = parseFloat(formData.preis_vk);
    if (formData.preis_ek) payload.preis_ek = parseFloat(formData.preis_ek);
    if (formData.qualitaetsstufe) payload.qualitaetsstufe = formData.qualitaetsstufe;
    // Beim Bearbeiten immer mitsenden, damit "nicht zugeordnet" die Bindung löst
    if (editingArtikel || formData.steuersatz_id !== '') {
      payload.steuersatz_id = formData.steuersatz_id === '' ? null : parseInt(formData.steuersatz_id);
    }
    if (formData.hinweistext) payload.hinweistext = formData.hinweistext;
    if (formData.standard_aufschlag_prozent) {
      payload.standard_aufschlag_prozent = parseFloat(formData.standard_aufschlag_prozent);
    }
    // Metallzuschlag (AP 3.5): leerer String heisst "nicht gepflegt" -> null,
    // die 0 muss aber durchkommen (Hohlpreis). Beim Bearbeiten werden die
    // Felder immer mitgesendet, damit Leeren auch wirklich loescht.
    if (editingArtikel || formData.kupfer_gewicht_kg_100m !== '') {
      payload.kupfer_gewicht_kg_100m =
        formData.kupfer_gewicht_kg_100m === '' ? null : parseFloat(formData.kupfer_gewicht_kg_100m);
    }
    if (editingArtikel || formData.kupfer_basis !== '') {
      payload.kupfer_basis =
        formData.kupfer_basis === '' ? null : parseFloat(formData.kupfer_basis);
    }
    if (Object.keys(customAttributes).length > 0) payload.custom_attributes = customAttributes;

    try {
      if (editingArtikel) {
        await axios.put(`${API_URL}/api/artikel/${editingArtikel.artikel_id}`, payload);
      } else {
        await axios.post(`${API_URL}/api/artikel`, payload);
      }
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Ein Fehler ist aufgetreten');
    } finally {
      setLoading(false);
    }
  };

  const handleAddKomponente = async () => {
    if (!editingArtikel) return;
    if (!neueKomponente.komponente_id) {
      setError('Bitte einen Artikel als Komponente auswählen');
      return;
    }
    setKompLoading(true);
    setError('');
    try {
      const res = await axios.post(
        `${API_URL}/api/artikel/${editingArtikel.artikel_id}/komponenten`,
        {
          komponente_id: parseInt(neueKomponente.komponente_id),
          menge: parseFloat(neueKomponente.menge) || 1,
        }
      );
      setKomponenten(prev => [...prev, res.data]);
      setNeueKomponente({ komponente_id: '', menge: '1' });
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Fehler beim Hinzufügen der Komponente');
    } finally {
      setKompLoading(false);
    }
  };

  const handleRemoveKomponente = async (k: ArtikelKomponente) => {
    if (!editingArtikel) return;
    try {
      await axios.delete(`${API_URL}/api/artikel/${editingArtikel.artikel_id}/komponenten/${k.id}`);
      setKomponenten(prev => prev.filter(x => x.id !== k.id));
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Fehler beim Entfernen der Komponente');
    }
  };

  // Komponenten-Auswahl: aktuellen Artikel ausschließen
  const verfuegbareArtikel = alleArtikel.filter(
    a => a.artikel_id !== editingArtikel?.artikel_id
  );

  return (
    <div className="artikel-form-container">
      <h2>{editingArtikel ? 'Artikel bearbeiten' : 'Neuer Artikel'}</h2>

      <form onSubmit={handleSubmit} className="artikel-form">
        {error && <div className="error-message">{error}</div>}

        <div className="form-group">
          <label>Bezeichnung *</label>
          <input
            type="text"
            value={formData.bezeichnung}
            onChange={(e) => setFormData({ ...formData, bezeichnung: e.target.value })}
            placeholder="z.B. Schraube M8x20 Edelstahl"
            required
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Kategorie *</label>
            <select
              value={formData.kategorie}
              onChange={(e) => setFormData({ ...formData, kategorie: e.target.value })}
            >
              <option value="ROHMATERIAL">Rohmaterial</option>
              <option value="HALBFERTIG">Halbfertig</option>
              <option value="FERTIGPRODUKT">Fertigprodukt</option>
              <option value="HILFSSTOFF">Hilfsstoff</option>
            </select>
          </div>

          <div className="form-group">
            <label>Variante / Ausführung</label>
            <input
              type="text"
              value={formData.variante}
              onChange={(e) => setFormData({ ...formData, variante: e.target.value })}
              placeholder="z.B. Premium-Material, Rot, Edelstahl V2A"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Einheit *</label>
            <select
              value={formData.einheit}
              onChange={(e) => setFormData({ ...formData, einheit: e.target.value })}
            >
              <option value="m\u00B3">m\u00B3</option>
              <option value="m\u00B2">m\u00B2</option>
              <option value="St\u00FCck">Stück</option>
              <option value="kg">kg</option>
              <option value="Liter">Liter</option>
            </select>
          </div>

          <div className="form-group">
            <label>Qualitätsstufe</label>
            <select
              value={formData.qualitaetsstufe}
              onChange={(e) => setFormData({ ...formData, qualitaetsstufe: e.target.value })}
            >
              <option value="">-- Keine --</option>
              <option value="A">A</option>
              <option value="B">B</option>
              <option value="C">C</option>
            </select>
          </div>
        </div>

        <div className="form-group">
          <label>Mindestbestand</label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={formData.mindestbestand}
            onChange={(e) => setFormData({ ...formData, mindestbestand: e.target.value })}
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Einkaufspreis (EUR)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={formData.preis_ek}
              onChange={(e) => setFormData({ ...formData, preis_ek: e.target.value })}
              placeholder="0.00"
            />
          </div>

          <div className="form-group">
            <label>Verkaufspreis (EUR)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={formData.preis_vk}
              onChange={(e) => setFormData({ ...formData, preis_vk: e.target.value })}
              placeholder="0.00"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Steuersatz</label>
            <select
              value={formData.steuersatz_id}
              onChange={(e) => setFormData({ ...formData, steuersatz_id: e.target.value })}
            >
              <option value="">– nicht zugeordnet –</option>
              {steuersaetze
                .filter(s => s.aktiv || String(s.steuersatz_id) === formData.steuersatz_id)
                .map(s => (
                  <option key={s.steuersatz_id} value={String(s.steuersatz_id)}>
                    {s.bezeichnung} ({s.prozent.toLocaleString('de-DE')} %){s.aktiv ? '' : ' – inaktiv'}
                  </option>
                ))}
            </select>
          </div>

          <div className="form-group">
            <label>Standard-Aufschlag (%)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={formData.standard_aufschlag_prozent}
              onChange={(e) => setFormData({ ...formData, standard_aufschlag_prozent: e.target.value })}
              placeholder="z.B. 25"
            />
          </div>
        </div>

        {/* Metallzuschlag (AP 3.5) — nur fuer Kabel/Leitungen relevant, deshalb
            optional und mit erklaerendem Hinweis statt kommentarlosem Zahlenfeld. */}
        <div className="form-row">
          <div className="form-group">
            <label>Kupferzahl (kg / 100 m)</label>
            <input
              type="number"
              step="0.001"
              min="0"
              value={formData.kupfer_gewicht_kg_100m}
              onChange={(e) => setFormData({ ...formData, kupfer_gewicht_kg_100m: e.target.value })}
              placeholder="z.B. 1.3 (= 13 kg/km)"
            />
          </div>

          <div className="form-group">
            <label>Kupferbasis (EUR / 100 kg)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={formData.kupfer_basis}
              onChange={(e) => setFormData({ ...formData, kupfer_basis: e.target.value })}
              placeholder="z.B. 150 — 0 = Hohlpreis"
            />
          </div>
        </div>
        <small style={{ display: 'block', marginTop: -8, marginBottom: 12, color: 'var(--ink-2)' }}>
          Nur für Kabel/Leitungen: beide Felder gesetzt = Metallartikel, der Kupferzuschlag wird
          aus der Tagesnotierung berechnet und auf dem Beleg getrennt ausgewiesen.
          Kupferbasis 0 bedeutet Hohlpreis (Listenpreis enthält kein Kupfer). Beide Felder leer
          lassen, wenn der Artikel kein Metallartikel ist.
        </small>

        <div className="form-group">
          <label>Hinweistext</label>
          <textarea
            value={formData.hinweistext}
            onChange={(e) => setFormData({ ...formData, hinweistext: e.target.value })}
            placeholder="Zusätzlicher Hinweis, erscheint z.B. auf Angeboten/Belegen"
            rows={2}
          />
        </div>

        <CustomFieldsForm
          tabelle="artikel"
          authToken={authToken}
          values={customAttributes}
          onChange={setCustomAttributes}
        />

        {/* Verkaufsartikel-Toggle */}
        <div className="form-group verkaufsartikel-toggle">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={formData.ist_verkaufsartikel}
              onChange={(e) => setFormData({ ...formData, ist_verkaufsartikel: e.target.checked })}
            />
            <span className="checkbox-text">
              <strong>Verkaufsartikel</strong>
              <small>Dieser Artikel wird aktiv an Kunden verkauft und erhält ein VERKAUF-Badge</small>
            </span>
          </label>
        </div>

        <div className="form-actions">
          <button type="button" onClick={onCancel} className="btn-secondary">
            Abbrechen
          </button>
          <button type="submit" disabled={loading} className="btn-primary">
            {loading ? 'Speichere...' : editingArtikel ? 'Aktualisieren' : 'Erstellen'}
          </button>
        </div>
      </form>

      {/* Stücklisten-Bereich (nur bei bestehenden Verkaufsartikeln) */}
      {editingArtikel && formData.ist_verkaufsartikel && (
        <div className="stueckliste-section">
          <h3>Stückliste (benötigte Materialien)</h3>
          <p className="stueckliste-hint">
            Definiere, welche Rohmaterialien oder Halbfertigprodukte für diesen Verkaufsartikel benötigt werden.
          </p>

          {komponenten.length > 0 && (
            <table className="stueckliste-table">
              <thead>
                <tr>
                  <th>Material</th>
                  <th>Menge</th>
                  <th>Einheit</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {komponenten.map((k) => (
                  <tr key={k.id}>
                    <td>{k.bezeichnung}</td>
                    <td>{k.menge}</td>
                    <td>{k.einheit}</td>
                    <td>
                      <button
                        type="button"
                        className="btn-delete-small"
                        onClick={() => handleRemoveKomponente(k)}
                        title="Entfernen"
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div className="add-komponente-row">
            <select
              value={neueKomponente.komponente_id}
              onChange={(e) => setNeueKomponente({ ...neueKomponente, komponente_id: e.target.value })}
            >
              <option value="">-- Material auswählen --</option>
              {verfuegbareArtikel.map(a => (
                <option key={a.artikel_id} value={a.artikel_id}>
                  {a.bezeichnung} ({a.einheit})
                </option>
              ))}
            </select>
            <input
              type="number"
              step="0.01"
              min="0.01"
              value={neueKomponente.menge}
              onChange={(e) => setNeueKomponente({ ...neueKomponente, menge: e.target.value })}
              placeholder="Menge"
              className="menge-input"
            />
            <button
              type="button"
              className="btn-primary"
              onClick={handleAddKomponente}
              disabled={kompLoading || !neueKomponente.komponente_id}
            >
              + Hinzufügen
            </button>
          </div>
        </div>
      )}

      {editingArtikel && !formData.ist_verkaufsartikel && (
        <div className="stueckliste-hint-box">
          Als Verkaufsartikel markieren, um eine Stückliste hinterlegen zu können.
        </div>
      )}

      {editingArtikel && (
        <div style={{ marginTop: 24 }}>
          <AktivitaetenBlock entitaetTyp="artikel" entitaetId={editingArtikel.artikel_id} />
        </div>
      )}
    </div>
  );
};

export default ArtikelForm;
